import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '../../../services/auth.service';
import Swal from 'sweetalert2';
import './index.css';

const PasswordExpired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = location.state?.email || '';

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[+!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push('Mínimo 8 caracteres');
    if (!hasUpperCase) errors.push('Al menos una mayúscula');
    if (!hasLowerCase) errors.push('Al menos una minúscula');
    if (!hasNumbers) errors.push('Al menos un número');
    if (!hasSpecialChar) errors.push('Al menos un carácter especial');

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo específico
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validaciones
    /* if (!formData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    } */

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else {
      const passwordErrors = validatePassword(formData.newPassword);
      if (passwordErrors.length > 0) {
        newErrors.newPassword = passwordErrors.join(', ');
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Debe confirmar la contraseña';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña debe ser diferente a la actual';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const userId = location.state?.userId;
      const response = await authService.passwordExpired(
        userId,
        formData.newPassword,
        formData.confirmPassword
      )

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.message === "PASSWORD_REUSED") {
          throw new Error('No puedes reutilizar una contraseña anterior. Por favor, elige una contraseña diferente.');
        } else {
          throw new Error(errorData.message || 'Error al actualizar contraseña');
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Contraseña Actualizada',
        text: 'Su contraseña ha sido actualizada exitosamente. Por favor, inicie sesión nuevamente.',
        confirmButtonText: 'Aceptar'
      }).then(() => {
        navigate('/');
      });

    } catch (error) {
      setErrors({
        submit: error.message || 'Error al actualizar la contraseña. Intente nuevamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="password-expired-container">
      <div className="password-expired-card">
        <div className="expired-header">
          <i className="icon-warning">⚠️</i>
          <h2>Contraseña Caducada</h2>
          <p>Tu contraseña ha expirado. Por favor, actualízala para continuar.</p>
        </div>

        <form onSubmit={handleSubmit} className="password-form">
          {/* <div className="form-group">
            <label htmlFor="currentPassword">Contraseña Actual</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? "text" : "password"}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                disabled={loading}
                className={errors.currentPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => togglePasswordVisibility('current')}
              >
                {showPasswords.current ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.currentPassword && (
              <span className="error-message">{errors.currentPassword}</span>
            )}
          </div> */}

          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                disabled={loading}
                className={errors.newPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswords.new ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.newPassword && (
              <span className="error-message">{errors.newPassword}</span>
            )}
            <div className="password-requirements">
              <small>La contraseña debe contener:</small>
              <ul>
                <li>Mínimo 8 caracteres</li>
                <li>Al menos una mayúscula y una minúscula</li>
                <li>Al menos un número</li>
                <li>Al menos un carácter especial (+!@#$%^&*)</li>
                <li>No reutilizar contraseñas anteriores</li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nueva Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                className={errors.confirmPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="error-message">{errors.confirmPassword}</span>
            )}
          </div>

          {errors.submit && (
            <div className="alert alert-error">{errors.submit}</div>
          )}

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordExpired;